import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useSelector } from "react-redux";
import styles from "../../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import modalStyles from "./Alllessonplanapprovallist.module.css";
import AxiosInstance, { TokenService } from "../../../../../services/Api";
import { CustomToggle } from "../../../../../components";
import CustomMRTTable from "../../../../../components/CustomMRTTable/CustomMRTTable";
import { buildLessonPlanApprovalColumns } from "./lessonPlanApprovalColumns";
import LessonPlanApprovalHistoryPanel from "./LessonPlanApprovalHistoryPanel";
import useLessonPlanApprovalData from "./useLessonPlanApprovalData";

// Text -> numeric code mapping
const STATUS_TEXT_TO_CODE = {
  Draft: 0,
  Submitted: 1,
  Submit: 1,
  Approved: 2,
  Approve: 2,
  Rejected: 3,
  Reject: 3,
  Resubmitted: 4,
  Resubmit: 4,
  Completed: 5,
};

const pickHmId = (rowData) => {
  if (!rowData || typeof rowData !== "object") return null;
  const candidates = [rowData.id,
  ];
  for (const c of candidates) {
    if (c != null && c !== "" && !Number.isNaN(Number(c))) return Number(c);
  }
  return null;
};

/* ==================================================================
   Rejection Reason Modal
   ================================================================== */
const RejectionReasonModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  translations,
}) => {
  const t = translations?.labels || {};
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setReason("");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(t.reasonRequired || "Please provide a reason for rejection");
      return;
    }
    if (typeof onConfirm === "function") onConfirm(trimmed);
  };

  const handleChange = (e) => {
    setReason(e.target.value);
    if (error) setError("");
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose?.();
  };

  return (
    <div className={modalStyles.backdrop} onMouseDown={handleBackdropClick}>
      <div
        className={modalStyles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rejection-reason-title"
      >
        <div className={modalStyles.header}>
          <h3 id="rejection-reason-title" className={modalStyles.title}>
            {t.reasonForRejection || "Reason for Rejection"}
          </h3>
          <button
            type="button"
            className={modalStyles.closeBtn}
            onClick={onClose}
            disabled={loading}
            aria-label={t.close || "Close"}
          >
            <IoClose />
          </button>
        </div>

        <div className={modalStyles.body}>
          <label
            className={modalStyles.inputLabel}
            htmlFor="rejection-reason-input"
          >
            {t.reason || "Reason"}
          </label>
          <input
            id="rejection-reason-input"
            type="text"
            className={`${modalStyles.input} ${
              error ? modalStyles.inputError : ""
              }`}
            value={reason}
            onChange={handleChange}
            placeholder={t.enterReasonPlaceholder || ""}
            disabled={loading}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirm();
              if (e.key === "Escape") onClose?.();
            }}
          />
          {error && <div className={modalStyles.errorText}>{error}</div>}
        </div>

        <div className={modalStyles.footer}>
          <button
            type="button"
            className={`${modalStyles.btn} ${modalStyles.cancelBtn}`}
            onClick={onClose}
            disabled={loading}
          >
            {t.cancel || "Cancel"}
          </button>
          <button
            type="button"
            className={`${modalStyles.btn} ${modalStyles.confirmBtn}`}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading
              ? t.submitting || "Submitting..."
              : t.yesReject || "Yes, Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ==================================================================
   Main list component
   ================================================================== */
function AllLessonPlanApprovalList() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const roleId = Number(user?.role?.id);
  const updated_by = Number(user?.id) || 0;

  const [weekFilter, setWeekFilter] = useState("this_week");
  const [rejectModal, setRejectModal] = useState({ open: false, row: null });
  const [submitting, setSubmitting] = useState(false);

  // Map<rowId, number> — incremented when that specific row's <select>
  // needs to be reverted to its row.original.status_text.
  // The Status cell uses this number as part of its key, so React
  // unmounts the old <select> (which is showing "Rejected") and mounts
  // a fresh one initialized from row.original.status_text ("Submitted").
  const [revertSignals, setRevertSignals] = useState({});

  const {
    data,
    loading,
    rowCount,
    pagination,
    setPagination,
    columnFilters,
    handleColumnFiltersChange,
    topicOptions,
    subTopicOptions,
    refetch,
  } = useLessonPlanApprovalData("", weekFilter);

  const handleView = (row) => {
    const statusTextLower = String(row?.status_text || "").toLowerCase();
    const isLocked =
      statusTextLower === "approved" ||
      statusTextLower === "rejected" ||
      statusTextLower === "completed" ||
      [2, 3, 5].includes(Number(row?.status));

    navigate("/user/lessonplanview", {
      state: {
        ID: row.id,
        hm_id:row.hm_id,
        data: row,
        status: row?.status,
        status_text: row?.status_text,
        isLocked,
      },
    });
  };

  const toggleOptions = [
    { label: translations?.labels?.toggleAll || "All", value: "all" },
    {
      label: translations?.labels?.toggleThisWeek || "This week",
      value: "this_week",
    },
  ];

  const callUpdateStatusApi = useCallback(
    async ({ rowData, status, comments = "" }) => {
      const def_id = rowData?.def_id;
      const hm_id = pickHmId(rowData);

      const payload = { def_id, status, comments, updated_by, hm_id };

      try {
        setSubmitting(true);
        const response = await AxiosInstance.put(
          `/book/update-lesson-plan-status`,
          payload,
        );

        if (response?.data?.status) {
          if (typeof refetch === "function") refetch();
        } else {
          console.error("Status update failed:", response?.data?.message);
        }
      } catch (e) {
        console.error("Status update error:", e);
      } finally {
        setSubmitting(false);
      }
    },
    [updated_by, refetch],
  );

  const handleStatusChange = useCallback(
    (rowData, newStatusText) => {
      const newStatusCode = STATUS_TEXT_TO_CODE[newStatusText];

      // Reject => open modal
      if (newStatusCode === 3) {
        setRejectModal({ open: true, row: rowData });
        return;
      }

      // Approve => call API
      if (newStatusCode === 2) {
        callUpdateStatusApi({ rowData, status: 2, comments: "" });
        return;
      }
    },
    [callUpdateStatusApi],
  );

  const handleRejectConfirm = async (reason) => {
    if (!rejectModal.row) return;
    await callUpdateStatusApi({
      rowData: rejectModal.row,
      status: 3,
      comments: reason,
    });
    setRejectModal({ open: false, row: null });
  };

  // Cancel => bump the revert signal for THIS row only.
  // The Status <select> for that row will re-mount and show
  // the original status_text again.
  const handleRejectCancel = () => {
    const rowId = rejectModal.row?.id;
    if (rowId != null) {
      setRevertSignals((prev) => ({
        ...prev,
        [rowId]: (prev[rowId] || 0) + 1,
      }));
    }
    setRejectModal({ open: false, row: null });
  };

  const handleDateRangePicked = () => {
    if (weekFilter !== "all") setWeekFilter("all");
  };

  const columns = useMemo(() => {
    const cols = buildLessonPlanApprovalColumns({
      translations,
      topicOptions,
      subTopicOptions,
      includeSection: false,
      includeScheduledDate: false,
      onStatusChange: handleStatusChange,
      onDateRangePicked: handleDateRangePicked,
      // pass map down so the Status cell can build its key from it
      revertSignals,
    });

    cols.push({
      id: "actions",
      header: translations?.labels?.action || "Action",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <button
          className={styles.viewBtn}
          onClick={() => handleView(row.original)}
          title={translations?.labels?.view || "View"}
        >
          <FaEye />
        </button>
      ),
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    translations,
    topicOptions,
    subTopicOptions,
    handleStatusChange,
    revertSignals,
  ]);

  return (
    <>
      <div className={styles.toggleRow}>
        <CustomToggle
          options={toggleOptions}
          value={weekFilter}
          onChange={setWeekFilter}
        />
      </div>

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        hideGlobalSearch={true}
        enableExpanding={true}
        renderDetailPanel={({ row }) => (
          <LessonPlanApprovalHistoryPanel row={row} />
        )}
      />

      <RejectionReasonModal
        open={rejectModal.open}
        onClose={handleRejectCancel}
        onConfirm={handleRejectConfirm}
        loading={submitting}
        translations={translations}
      />
    </>
  );
}

export default AllLessonPlanApprovalList;
